import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAgents } from "@/components/Agents/AgentsContext";
import { agentsHref } from "@/lib/atwAgentsHost";
import { creditsRemaining, effectiveCreditsIncluded } from "@/lib/agentsDemoData";

export function CreditsPage() {
  const { org } = useAgents();
  const included = effectiveCreditsIncluded(org);
  const remaining = creditsRemaining(org);
  const pct = included > 0 ? Math.min(100, Math.round((org.creditsUsed / included) * 100)) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Credits</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Shared org pool for billable agent actions. Viewing UI and config does not burn work
          credits. Unit pricing is TBD.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Used</CardDescription>
            <CardTitle className="text-2xl">{org.creditsUsed.toLocaleString()}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Included</CardDescription>
            <CardTitle className="text-2xl">{included.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            Base {org.creditsIncluded.toLocaleString()}
            {org.maxAddon ? " · Max multiplies by 20" : ""}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Remaining</CardDescription>
            <CardTitle className="text-2xl">{remaining.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            {org.maxAddon ? (
              <Badge>Max on</Badge>
            ) : (
              <Badge variant="outline">Max off</Badge>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pool usage</CardTitle>
          <CardDescription>{pct}% of included credits used this month</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
            <div
              className="h-full rounded-full bg-gray-900 dark:bg-white"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Turn on Max or change plan via{" "}
            <Link to={agentsHref("/checkout")} className="underline underline-offset-2">
              Checkout
            </Link>{" "}
            when billing is wired. Burn by agent:
          </p>
          <ul className="mt-3 space-y-2">
            {org.agents
              .filter((agent) => agent.status !== "archived")
              .map((agent) => (
                <li
                  key={agent.id}
                  className="flex justify-between text-sm border-b border-gray-100 py-2 dark:border-white/10"
                >
                  <Link
                    to={agentsHref(`/agents/${agent.id}`)}
                    className="font-medium hover:underline"
                  >
                    {agent.name}
                  </Link>
                  <span>{agent.creditsBurnedMonth.toLocaleString()}</span>
                </li>
              ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
